import { z } from 'zod';
import { stagedBlobRefSchema } from '../routing/staged.js';
import { uploadMimeSchema } from './allowlist.js';
/**
 * A staged upload is its OWN document, not a field on the StepInstance.
 *
 * The staged artifact and the attested submission have different lifetimes. A person can
 * upload the wrong photograph three times before submitting once; the instance is the
 * document immutability protects, and mutating it on every discarded attempt would mean
 * the record HR approves has been rewritten repeatedly by work that was thrown away.
 *
 * Lives in the `instances` container (pk `/personId`) so an upload and the step it belongs
 * to are in the same partition and readable in one query.
 */
/**
 * Where a candidate's file stands with the malware scanner.
 *
 * EXTENDED, never renamed: `pending` is the quarantined state every new upload starts in and
 * `infected` is the malicious one. Both are already on stored records, and renaming them would
 * reinterpret documents that exist. `scanning` and `scan_failed` are the two the lifecycle was
 * missing — the middle of it, and the difference between a scanner that could not be reached
 * (`unavailable`, retried) and one that looked and produced no verdict (`scan_failed`, needs a
 * person). The rules that read these live in `uploads/scan.ts`.
 */
export const scanStateSchema = z.enum(['pending', 'scanning', 'clean', 'infected', 'scan_failed', 'unavailable']);
export const uploadStateSchema = z.enum(['reserved', 'accepted', 'rejected']);
/**
 * Why a completed upload was refused. Kept as a closed vocabulary because it is written to
 * an audit event, and free text in an audit event is how PII arrives in a compliance log.
 */
export const uploadRejectionSchema = z.enum([
    'missing',
    'size-mismatch',
    'signature-mismatch',
    'type-not-accepted',
    'too-large',
    'empty',
    /** A server-staged artifact whose submit was refused after staging (#122). */
    'not-submitted',
]);
/**
 * How bytes reached quarantine. The same words as a FileRecord's `source` for the three that
 * pass through here; `migration` never reserves through an upload route.
 */
export const uploadSourceSchema = z.enum(['portal-upload', 'pdf-artifact', 'hr-upload']);
/**
 * The quarantine slot HR template sources are reserved under: the middle segment of their blob
 * name, in the agency-scope partition. It is a slot, not a step — nothing is ever issued with it.
 */
export const TEMPLATE_SOURCE_SLOT = 'template-source';
export const uploadRecordSchema = z.object({
    id: z.string().min(1),
    personId: z.string().min(1),
    docType: z.literal('upload').default('upload'),
    instanceId: z.string().min(1),
    state: uploadStateSchema,
    container: z.string().min(1),
    /** `<personId>/<instanceId>/<uploadId>` — opaque throughout. Never a filename. */
    blobName: z.string().min(1),
    declaredContentType: uploadMimeSchema,
    declaredByteLength: z.number().int().positive(),
    /** What the person called it. Blob metadata, never a path segment. */
    originalFileName: z.string().min(1),
    /**
     * Set only once the bytes have been re-read, measured and sniffed server-side. Absent on
     * a reservation, which is the difference between "a SAS was minted" and "a file we have
     * actually looked at".
     */
    staged: stagedBlobRefSchema.nullable().default(null),
    /**
     * Modelled now, enforced by the routing ticket. Defender for Storage is out of scope
     * here, so a value of `pending` is the honest state rather than an optimistic `clean`.
     */
    scan: scanStateSchema.default('pending'),
    rejection: uploadRejectionSchema.nullable().default(null),
    reservedAt: z.string().datetime(),
    completedAt: z.string().datetime().nullable().default(null),
    /** Versions of the validated bytes before and after sealing their HTTP headers.
     * Optional for retained uploads created before version-correlated scan intake. */
    contentEtags: z.array(z.string().min(1)).min(1).max(2).optional(),
    /** Cosmos TTL. A reservation nobody completes should not sit in the container forever. */
    ttl: z.number().int().positive().optional(),
    /**
     * Who put the bytes in quarantine (#188, ADR-009 §2). Absent on records written before HR
     * uploads existed, which were all the person's own portal uploads or server artifacts.
     */
    source: uploadSourceSchema.optional(),
    /** The Entra object id of the HR user who uploaded it — never their name. `hr-upload` only. */
    uploadedBy: z.string().min(1).optional(),
    /**
     * What the bytes were staged for (`hrUploadPurposeSchema`). Set at reservation and never changed,
     * so a consumer can refuse an upload meant for something else. Absent on records written before
     * it existed; those are evidence, and the signed-offer path requires the value explicitly.
     */
    purpose: z.enum(['record-evidence', 'signed-offer']).optional(),
    /**
     * The upload these bytes were generated FROM, for a server-built composite (a template source
     * with an appended signature page). Ids only; the composite is scanned like any other upload.
     */
    derivedFrom: z.string().min(1).optional(),
});
/** How long a reservation lives before Cosmos reaps it. Ten times the SAS window. */
export const UPLOAD_RESERVATION_TTL_SECONDS = 100 * 60;
/** The staging blob key. Every segment is an opaque id — see the comment on blobName. */
export function stagingBlobName(personId, instanceId, uploadId) {
    return `${personId}/${instanceId}/${uploadId}`;
}
//# sourceMappingURL=record.js.map